/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useRef, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import {
  FileSpreadsheet, AlertTriangle, Download, Upload,
  Loader2, ChevronLeft, ChevronDown, ChevronRight, Trash2, Plus, RefreshCw, CheckCircle2, Circle, Link2,
  Rows3, LayoutList, Search, X,
} from "lucide-react"
import { catalogueApi, ApiError, type CatalogueDTO, type CatalogueProductDTO, type DiffResultDTO, type CsvSchemaDTO, type CatalogueSearchResultDTO } from "@/lib/api"
import { toast } from "sonner"

// Mapping fixe SOS Lunettes — colonnes CSV → champs TwinModels
// Ref (clé unique) = marque + "_" + n_inventaire (calculé côté backend)
const FIELDS = [
  { key: "brand",        label: "Marque",          csv: "marque",       required: true },
  { key: "product_ref",  label: "Produit (ref)",   csv: "ref_modele",   required: true },
  { key: "inventory_id", label: "N° inventaire",   csv: "n_inventaire", required: true },
  { key: "gender",       label: "Sexe",            csv: "sexe",         required: false },
  { key: "forme",        label: "Forme",           csv: "forme",        required: false },
  { key: "avatar_type",  label: "Type (mannequin)", csv: "mannequin_ia", required: false },
]


// ── Catalogue list ────────────────────────────────────────────────────────────

function CatalogueList({
  onOpen, onImported,
}: {
  onOpen: (cat: CatalogueDTO) => void
  onImported: () => void
}) {
  const [catalogues, setCatalogues] = useState<CatalogueDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [showSchema, setShowSchema] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [separator, setSeparator] = useState<";" | ",">(";")
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [headers, setHeaders] = useState<string[]>([])
  const [schemaLoaded, setSchemaLoaded] = useState(false)
  const [listSort, setListSort] = useState<"name" | "date" | "count">("date")
  const fileRef = useRef<HTMLInputElement>(null)
  const pendingFiles = useRef<File[]>([])

  useEffect(() => {
    load()
    catalogueApi.getSchema().then((s: CsvSchemaDTO) => {
      if (s.mapping) { setMapping(s.mapping); setSchemaLoaded(true) }
      if (s.separator === ";" || s.separator === ",") setSeparator(s.separator)
    }).catch(() => {})
  }, [])

  async function load() {
    setLoading(true)
    try { setCatalogues(await catalogueApi.list()) }
    catch { setError("Impossible de charger les catalogues") }
    finally { setLoading(false) }
  }

  function readHeaders(file: File, sep: string) {
    const reader = new FileReader()
    reader.onload = e => {
      const text = e.target?.result as string
      const cols = text.split("\n")[0].split(sep).map(c => c.trim())
      setHeaders(cols)
      if (!schemaLoaded) {
        const auto: Record<string, string> = {}
        FIELDS.forEach(f => {
          const key = f.key.split("_")[0]
          const match = cols.find(c => c.toLowerCase().includes(key))
          if (match) auto[f.key] = match
        })
        setMapping(auto)
      }
      setShowSchema(true)
    }
    reader.readAsText(file)
  }

  function handleFileSelect(files: FileList | null) {
    if (!files?.length) return
    const arr = Array.from(files).filter(f => f.name.endsWith(".csv"))
    if (!arr.length) return
    pendingFiles.current = arr
    setError(null)
    // Always show mapping menu so user can verify/adjust — readHeaders also sets showSchema=true
    readHeaders(arr[0], separator)
  }

  async function doImport(files: File[]) {
    setImporting(true)
    setError(null)
    try {
      await catalogueApi.saveSchema(mapping, separator)
      setSchemaLoaded(true)
      setShowSchema(false)
      // Import each CSV as a separate catalogue sequentially
      for (const file of files) {
        await catalogueApi.import(file)
      }
      // Auto-link identical products across catalogues
      await catalogueApi.autoLink().catch(() => {})
      await load()
      onImported()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erreur lors de l'import")
    } finally {
      setImporting(false)
      pendingFiles.current = []
    }
  }

  async function handleDelete(id: number, e: React.MouseEvent) {
    e.stopPropagation()
    if (!confirm("Supprimer ce catalogue ?")) return
    try {
      await catalogueApi.delete(id)
      setCatalogues(p => p.filter(c => c.id !== id))
    } catch { setError("Erreur lors de la suppression") }
  }

  const requiredFilled = FIELDS.filter(f => f.required).every(f => mapping[f.key])

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }


  return (
    <div
      className={`flex flex-col gap-6 min-h-[200px] rounded-xl transition-colors ${dragOver ? "bg-secondary/60 ring-2 ring-border" : ""}`}
      onDragOver={e => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false) }}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Catalogue</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Un CSV = un catalogue. Glissez-déposez ou cliquez pour importer.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Separator */}
          <div className="flex items-center gap-1.5 mr-2">
            {([";", ","] as const).map(s => (
              <button key={s} onClick={() => setSeparator(s)}
                className={`px-2.5 py-1 rounded text-xs font-mono border transition-colors ${
                  separator === s ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                }`}>
                {s}
              </button>
            ))}
          </div>
          <input ref={fileRef} type="file" accept=".csv" multiple className="hidden" onChange={e => handleFileSelect(e.target.files)} />
          <Button size="sm" className="rounded-full gap-1.5" onClick={() => fileRef.current?.click()} disabled={importing}>
            {importing ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
            {importing ? "Import en cours…" : "Importer CSV"}
          </Button>
        </div>
      </div>

      {/* Schema mapping — shown on first import */}
      {showSchema && headers.length > 0 && (
        <div className="border border-border rounded-xl p-5 flex flex-col gap-4">
          <div>
            <p className="text-sm font-semibold">
              Mapping des colonnes
              {pendingFiles.current.length > 1 && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  — {pendingFiles.current.length} fichiers sélectionnés
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Sauvegardé automatiquement pour vos prochains imports.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(f => (
              <div key={f.key} className="flex flex-col gap-1">
                <label className="text-xs font-medium">
                  {f.label}{f.required && <span className="text-destructive ml-0.5">*</span>}
                </label>
                <select
                  className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
                  value={mapping[f.key] ?? ""}
                  onChange={e => setMapping(p => ({ ...p, [f.key]: e.target.value }))}
                >
                  <option value="">— ignorer —</option>
                  {headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="rounded-full" disabled={!requiredFilled || importing}
              onClick={() => pendingFiles.current.length && doImport(pendingFiles.current)}>
              {importing ? <><Loader2 className="size-3.5 animate-spin mr-1.5" />Import…</> : "Confirmer et importer"}
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setShowSchema(false); pendingFiles.current = [] }}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-lg px-3 py-2">
          <AlertTriangle className="size-4 shrink-0" />{error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="size-5 animate-spin mr-2" />Chargement…
        </div>
      ) : catalogues.length === 0 ? (
        <div
          className="border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:bg-secondary/40 transition-colors"
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="mx-auto mb-3 size-8 text-muted-foreground" />
          <p className="text-sm font-medium">Aucun catalogue — importer votre premier CSV</p>
          <p className="text-xs text-muted-foreground mt-1">Chaque CSV devient un catalogue indépendant</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex justify-end mb-1">
            <select
              value={listSort}
              onChange={e => setListSort(e.target.value as typeof listSort)}
              className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-muted-foreground"
            >
              <option value="date">Trier : Date</option>
              <option value="name">Trier : Nom</option>
              <option value="count">Trier : Nb produits</option>
            </select>
          </div>
          {[...catalogues].sort((a, b) =>
            listSort === "name" ? a.name.localeCompare(b.name) :
            listSort === "count" ? b.product_count - a.product_count :
            new Date(b.imported_at).getTime() - new Date(a.imported_at).getTime()
          ).map(cat => (
            <div
              key={cat.id}
              onClick={() => onOpen(cat)}
              className="flex items-center gap-4 px-4 py-3.5 border border-border rounded-xl hover:bg-secondary/40 cursor-pointer transition-colors group"
            >
              <FileSpreadsheet className="size-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{cat.name.replace(/_/g, ' ')}</p>
                  {cat.has_duplicates && (
                    <span className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded-full shrink-0">
                      <AlertTriangle className="size-2.5" />doublons
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{cat.source_file}</p>
              </div>
              <div className="flex flex-col items-end shrink-0">
                <span className="text-sm tabular-nums text-muted-foreground">{cat.product_count} produits</span>
                <span className="text-xs tabular-nums text-muted-foreground/60">{cat.image_count} images</span>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">
                {new Date(cat.imported_at).toLocaleDateString("fr-FR")}
              </span>
              <button
                className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                onClick={e => handleDelete(cat.id, e)}
              >
                <Trash2 className="size-4 text-muted-foreground hover:text-destructive transition-colors" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: CatalogueProductDTO["status"] }) {
  const map = {
    not_started: { label: "Non démarré", cls: "bg-secondary text-muted-foreground" },
    processing:  { label: "En cours",    cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
    done:        { label: "Terminé",     cls: "bg-green-500/10 text-green-600 dark:text-green-400" },
    error:       { label: "Erreur",      cls: "bg-red-500/10 text-red-600 dark:text-red-400" },
  } as const
  const { label, cls } = map[status] ?? map.not_started
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
}

// ── Product row ───────────────────────────────────────────────────────────────

function ProductRow({ p, collapsed, setCollapsed, onPatch, catalogueName }: {
  p: CatalogueProductDTO
  collapsed: Set<string>
  setCollapsed: React.Dispatch<React.SetStateAction<Set<string>>>
  onPatch: (id: number, updates: Partial<CatalogueProductDTO>) => void
  catalogueName?: string
}) {
  void collapsed; void setCollapsed // not used in flat row, kept for signature symmetry
  const [editingForme, setEditingForme] = useState(false)
  const [formeVal, setFormeVal] = useState(p.forme ?? '')

  const commitForme = async () => {
    setEditingForme(false)
    const trimmed = formeVal.trim() || null
    if (trimmed === (p.forme ?? null)) return
    try {
      await catalogueApi.patchProduct(p.id, { forme: trimmed })
      onPatch(p.id, { forme: trimmed })
    } catch { toast.error("Failed to update forme") }
  }

  return (
    <tr className={`hover:bg-secondary/40 ${p.is_duplicate ? "bg-amber-500/5" : ""}`}>
      {catalogueName !== undefined && <td className="px-3 py-2 text-xs text-primary/80 font-medium">{catalogueName}</td>}
      <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">{p.inventory_id ?? "—"}</td>
      <td className="px-3 py-2 font-mono text-xs">
        {p.ref_key.replace(/ /g, '_')}
        {p.is_duplicate && <AlertTriangle className="inline size-3 ml-1 text-amber-500" />}
        {p.link_group_id && (
          <span title={`Linked to ${p.linked_count} other source${p.linked_count !== 1 ? 's' : ''}`}>
            <Link2 className="inline size-3 ml-1.5 text-blue-500" />
            {p.linked_count > 0 && <span className="text-[10px] text-blue-500 ml-0.5">{p.linked_count}</span>}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-sm">{p.brand ?? <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-2 font-mono text-xs">{p.product_ref ? p.product_ref.replace(/ /g, '_') : <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{p.product_ref ? p.product_ref.replace(/ /g, '_').replace(/_[^_]+$/, '') : <span>—</span>}</td>
      <td className="px-3 py-2 text-sm">{p.gender ?? <span className="text-muted-foreground">—</span>}{p.gender?.toLowerCase().match(/^(kid|enfant)$/) && <span className="ml-1 text-[10px] bg-purple-500/10 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full">kid</span>}</td>
      <td className="px-3 py-2 text-sm min-w-[90px]">
        {editingForme ? (
          <input
            autoFocus
            className="w-full text-sm bg-background border border-border rounded px-1.5 py-0.5 outline-none focus:border-foreground/50"
            value={formeVal}
            onChange={e => setFormeVal(e.target.value)}
            onBlur={commitForme}
            onKeyDown={e => { if (e.key === 'Enter') commitForme(); if (e.key === 'Escape') { setFormeVal(p.forme ?? ''); setEditingForme(false) } }}
          />
        ) : (
          <button
            className="w-full text-left group flex items-center gap-1"
            onClick={() => { setFormeVal(p.forme ?? ''); setEditingForme(true) }}
            title="Click to edit"
          >
            {p.forme
              ? <span>{p.forme}</span>
              : <span className="text-muted-foreground/50 italic text-xs">+ add forme</span>}
          </button>
        )}
      </td>
      <td className="px-3 py-2">
        {p.avatar_type ? (
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            p.avatar_type === "Optique + Solaire" ? "bg-blue-500/10 text-blue-600 dark:text-blue-400" :
            p.avatar_type === "Solaire" ? "bg-amber-500/10 text-amber-600 dark:text-amber-400" :
            "bg-secondary text-muted-foreground"
          }`}>{p.avatar_type}</span>
        ) : <span className="text-muted-foreground text-xs">—</span>}
      </td>
      <td className="px-3 py-2 text-center">
        {p.images_uploaded
          ? <CheckCircle2 className="size-4 text-green-500 mx-auto" />
          : <Circle className="size-4 text-muted-foreground/30 mx-auto" />}
      </td>
      <td className="px-3 py-2 text-center text-xs tabular-nums text-muted-foreground">
        {p.avatar_type === "Optique + Solaire" ? 2 : 1}
      </td>
      <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
    </tr>
  )
}

// ── Tree view: Marque > Forme > Ref > Produits ────────────────────────────────

function TreeView({ products, collapsed, setCollapsed }: {
  products: CatalogueProductDTO[]
  collapsed: Set<string>
  setCollapsed: React.Dispatch<React.SetStateAction<Set<string>>>
}) {
  // Build hierarchy: brand → forme → product_ref → products[]
  const tree = products.reduce<Record<string, Record<string, Record<string, CatalogueProductDTO[]>>>>((acc, p) => {
    const brand = p.brand ?? "—"
    const forme = p.forme ?? "—"
    const ref   = p.product_ref ?? "—"
    if (!acc[brand]) acc[brand] = {}
    if (!acc[brand][forme]) acc[brand][forme] = {}
    if (!acc[brand][forme][ref]) acc[brand][forme][ref] = []
    acc[brand][forme][ref].push(p)
    return acc
  }, {})

  function toggle(key: string) {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      localStorage.setItem("cat_collapsed", JSON.stringify([...next]))
      return next
    })
  }

  return (
    <div className="border border-border rounded-xl overflow-hidden divide-y divide-border">
      {Object.entries(tree).sort(([a], [b]) => a.localeCompare(b)).map(([brand, formes]) => {
        const brandKey = `tree-brand-${brand}`
        const brandTotal = Object.values(formes).flatMap(refs => Object.values(refs)).flat().length
        return (
          <div key={brand}>
            <button
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-secondary/70 hover:bg-secondary text-left select-none"
              onClick={() => toggle(brandKey)}
            >
              {collapsed.has(brandKey) ? <ChevronRight className="size-3.5 shrink-0" /> : <ChevronDown className="size-3.5 shrink-0" />}
              <span className="font-semibold text-sm">{brand}</span>
              <span className="text-xs text-muted-foreground ml-1">({brandTotal} produits)</span>
            </button>
            {!collapsed.has(brandKey) && Object.entries(formes).sort(([a], [b]) => a.localeCompare(b)).map(([forme, refs]) => {
              const formeKey = `tree-forme-${brand}-${forme}`
              const formeTotal = Object.values(refs).flat().length
              return (
                <div key={forme} className="border-t border-border/50">
                  <button
                    className="w-full flex items-center gap-2 pl-8 pr-4 py-2 bg-secondary/30 hover:bg-secondary/50 text-left select-none"
                    onClick={() => toggle(formeKey)}
                  >
                    {collapsed.has(formeKey) ? <ChevronRight className="size-3 shrink-0" /> : <ChevronDown className="size-3 shrink-0" />}
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{forme}</span>
                    <span className="text-xs text-muted-foreground/60 ml-1">({formeTotal})</span>
                  </button>
                  {!collapsed.has(formeKey) && Object.entries(refs).sort(([a], [b]) => a.localeCompare(b)).map(([ref, items]) => {
                    const refKey = `tree-ref-${brand}-${forme}-${ref}`
                    return (
                      <div key={ref} className="border-t border-border/30">
                        <button
                          className="w-full flex items-center gap-2 pl-14 pr-4 py-1.5 hover:bg-secondary/30 text-left select-none"
                          onClick={() => toggle(refKey)}
                        >
                          {collapsed.has(refKey) ? <ChevronRight className="size-3 shrink-0 text-muted-foreground" /> : <ChevronDown className="size-3 shrink-0 text-muted-foreground" />}
                          <span className="font-mono text-xs">{ref}</span>
                          <span className="text-xs text-muted-foreground/60 ml-1">({items.length})</span>
                        </button>
                        {!collapsed.has(refKey) && (
                          <div className="pl-20 pr-4 py-1.5 flex flex-wrap gap-1.5">
                            {items.map(p => (
                              <span key={p.id} className="inline-flex items-center gap-1 text-xs bg-secondary px-2 py-0.5 rounded-md font-mono">
                                {p.inventory_id ?? p.ref_key}
                                {p.avatar_type === "Optique + Solaire" && <span className="text-blue-500">×2</span>}
                                {p.is_duplicate && <AlertTriangle className="size-2.5 text-amber-500" />}
                                <StatusBadge status={p.status} />
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}


// ── Catalogue detail ──────────────────────────────────────────────────────────

function CatalogueDetail({
  catalogue, onBack, onUpdated, search = "",
}: {
  catalogue: CatalogueDTO
  onBack: () => void
  onUpdated: () => void
  search?: string
}) {
  const [products, setProducts] = useState<CatalogueProductDTO[]>([])
  const patchProduct = (id: number, updates: Partial<CatalogueProductDTO>) =>
    setProducts(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p))
  const [loading, setLoading] = useState(true)
  const [diff, setDiff] = useState<DiffResultDTO | null>(null)
  const [diffFile, setDiffFile] = useState<File | null>(null)
  const [applyLoading, setApplyLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<"inventory" | "brand" | "status">(() =>
    (localStorage.getItem("cat_sortBy") as "inventory" | "brand" | "status") ?? "inventory"
  )
  const [groupBy, setGroupBy] = useState<"none" | "gender" | "forme" | "base_ref" | "avatar_type" | "images_uploaded">(() =>
    (localStorage.getItem("cat_groupBy") as "none" | "gender" | "forme" | "base_ref" | "avatar_type" | "images_uploaded") ?? "none"
  )
  const [treeView, setTreeView] = useState(() => localStorage.getItem("cat_treeView") === "1")
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem("cat_collapsed") ?? "[]")) }
    catch { return new Set() }
  })
  const [tableView, setTableView] = useState<"scroll" | "page">(() =>
    (localStorage.getItem("cat_tableView") as "scroll" | "page") ?? "scroll"
  )
  const [quickFilter, setQuickFilter] = useState<"all" | "duplicates" | "linked">("all")
  const PAGE_SIZE = 50
  const [page, setPage] = useState(1)
  const diffFileRef = useRef<HTMLInputElement>(null)

  useEffect(() => { load() }, [catalogue.id])
  useEffect(() => { setPage(1) }, [search])

  async function load() {
    setLoading(true)
    try {
      await catalogueApi.autoLink().catch(() => {})
      setProducts(await catalogueApi.products(catalogue.id))
    }
    catch { setError("Impossible de charger les produits") }
    finally { setLoading(false) }
  }

  async function handleDiffFile(files: FileList | null) {
    if (!files?.length) return
    const file = files[0]
    setDiffFile(file)
    setError(null)
    try {
      const result = await catalogueApi.diff(catalogue.id, file)
      setDiff(result)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erreur lors de l'analyse")
    }
  }

  async function handleApply(apply: string[]) {
    if (!diffFile) return
    setApplyLoading(true)
    try {
      await catalogueApi.applyDiff(catalogue.id, diffFile, apply)
      await catalogueApi.autoLink().catch(() => {})
      setDiff(null)
      setDiffFile(null)
      await load()
      onUpdated()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erreur lors de l'application")
    } finally {
      setApplyLoading(false)
    }
  }

  function handleExport() {
    const url = catalogueApi.exportUrl(catalogue.id)
    const token = localStorage.getItem("access_token")
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`)
        return r.blob()
      })
      .then(blob => {
        const a = document.createElement("a")
        a.href = URL.createObjectURL(blob)
        a.download = `${catalogue.name}_clean.csv`
        a.click()
        URL.revokeObjectURL(a.href)
      })
      .catch(err => toast.error(err instanceof Error ? err.message : "Export failed"))
  }

  const STATUS_ORDER = { error: 0, processing: 1, not_started: 2, done: 3 }

  const filtered = products
    .filter(p => {
      if (quickFilter === "duplicates" && !p.is_duplicate) return false
      if (quickFilter === "linked" && !p.link_group_id) return false
      return !search || p.ref_key.toLowerCase().includes(search.toLowerCase()) ||
        p.brand?.toLowerCase().includes(search.toLowerCase()) ||
        p.product_ref?.toLowerCase().includes(search.toLowerCase())
    })
    .sort((a, b) => {
      if (sortBy === "inventory") return (parseInt(a.inventory_id ?? "0") || 0) - (parseInt(b.inventory_id ?? "0") || 0)
      if (sortBy === "brand") return (a.brand ?? "").localeCompare(b.brand ?? "")
      if (sortBy === "status") return (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
      return 0
    })

  const grouped: { label: string | null; items: typeof filtered }[] =
    groupBy === "none"
      ? [{ label: null, items: filtered }]
      : Object.entries(
          filtered.reduce<Record<string, typeof filtered>>((acc, p) => {
            const key = groupBy === "gender" ? (p.gender ?? "—")
              : groupBy === "forme" ? (p.forme ?? "—")
              : groupBy === "base_ref" ? (p.product_ref?.replace(/ /g, '_').replace(/_[^_]+$/, '') ?? "—")
              : groupBy === "images_uploaded" ? (p.images_uploaded ? "Uploadé" : "Non uploadé")
              : (p.avatar_type ?? "—")
            ;(acc[key] ??= []).push(p)
            return acc
          }, {})
        )
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([label, items]) => ({ label, items }))

  // Pagination — only applies in "page" view, and only when groupBy === "none"
  const totalPages = tableView === "page" && groupBy === "none" ? Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)) : 1
  const pagedItems = tableView === "page" && groupBy === "none"
    ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : null

  const dupCount    = products.filter(p => p.is_duplicate).length
  const linkedCount = products.filter(p => !!p.link_group_id).length
  const doneCount = products.filter(p => p.status === "done").length
  const totalImages = products.reduce((acc, p) => acc + (p.avatar_type === "Optique + Solaire" ? 2 : 1), 0)
  const doneImages = products.filter(p => p.status === "done").reduce((acc, p) => acc + (p.avatar_type === "Optique + Solaire" ? 2 : 1), 0)

  return (
    <div className="flex flex-col gap-5">
      {/* Sticky top bar — header + search */}
      <div className="sticky top-0 z-10 -mx-6 px-6 pt-1 pb-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex flex-col gap-3 border-b border-border/60">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="size-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold tracking-tight truncate">{catalogue.name.replace(/_/g, ' ')}</h1>
            <p className="text-xs text-muted-foreground">{catalogue.source_file} — {catalogue.product_count} produits</p>
            {products.length > 0 && (
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-muted-foreground">
                  Produits traités : <span className="font-semibold text-foreground tabular-nums">{doneCount}/{products.length}</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  Images générées : <span className="font-semibold text-foreground tabular-nums">{doneImages}/{totalImages}</span>
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input ref={diffFileRef} type="file" accept=".csv" className="hidden" onChange={e => handleDiffFile(e.target.files)} />
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => diffFileRef.current?.click()}>
              <RefreshCw className="size-3.5" />Réimporter
            </Button>
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={async () => {
              try {
                const r = await catalogueApi.backfillForme()
                await load()
                if (r.updated > 0) setError(null)
              } catch { setError("Erreur backfill Forme") }
            }}>
              <RefreshCw className="size-3.5" />Sync Forme
            </Button>
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={handleExport}>
              <Download className="size-3.5" />Exporter CSV
            </Button>
          </div>
        </div>

        {/* Sort + group */}
        {products.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <select
              className="text-xs border border-border rounded-lg px-2 py-2 bg-background text-muted-foreground"
              value={sortBy}
              onChange={e => { const v = e.target.value as typeof sortBy; setSortBy(v); localStorage.setItem("cat_sortBy", v) }}
            >
              <option value="inventory">Trier : N° inv</option>
              <option value="brand">Trier : Marque</option>
              <option value="status">Trier : Statut</option>
            </select>
            <select
              className="text-xs border border-border rounded-lg px-2 py-2 bg-background text-muted-foreground"
              value={groupBy}
              onChange={e => {
                const v = e.target.value as typeof groupBy
                setGroupBy(v); localStorage.setItem("cat_groupBy", v)
                setCollapsed(new Set()); localStorage.setItem("cat_collapsed", "[]")
                setPage(1)
              }}
            >
              <option value="none">Grouper : aucun</option>
              <option value="gender">Grouper : Sexe</option>
              <option value="forme">Grouper : Forme</option>
              <option value="base_ref">Grouper : Base ref</option>
              <option value="avatar_type">Grouper : Type</option>
              <option value="images_uploaded">Grouper : Upload</option>
            </select>
            {/* Quick filters */}
            <div className="flex items-center gap-1.5">
              {([
                { id: "all",        label: "All",       count: products.length,                        color: "" },
                { id: "duplicates", label: "Doublons",  count: dupCount,    hidden: dupCount === 0,    color: "text-amber-600 dark:text-amber-400" },
                { id: "linked",     label: "Linked",    count: linkedCount, hidden: linkedCount === 0, color: "text-blue-500" },
              ] as const).filter(f => !("hidden" in f && f.hidden)).map(f => (
                <button key={f.id} onClick={() => { setQuickFilter(f.id); setPage(1) }}
                  className={["flex items-center gap-1 px-2.5 h-7 rounded-full text-xs font-medium border transition-colors",
                    quickFilter === f.id
                      ? "bg-foreground text-background border-foreground"
                      : `border-border hover:border-foreground/40 ${f.color}`
                  ].join(" ")}>
                  {f.label}
                  <span className={["tabular-nums text-[10px]", quickFilter === f.id ? "opacity-70" : "opacity-60"].join(" ")}>
                    {f.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-1.5">
              <button
                title="Vue arborescente (Marque › Forme › Ref › Produits)"
                onClick={() => { const v = !treeView; setTreeView(v); localStorage.setItem("cat_treeView", v ? "1" : "0") }}
                className={["px-2.5 h-7 rounded-lg border text-xs font-medium transition-colors flex items-center gap-1",
                  treeView ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"
                ].join(" ")}
              >
                <ChevronRight className="size-3" />Arbre
              </button>
              <div className="flex rounded-lg border border-border overflow-hidden">
                {([
                  { id: "scroll", icon: <Rows3 className="size-3.5" />,    title: "Scroll (all)" },
                  { id: "page",   icon: <LayoutList className="size-3.5" />, title: "Pages" },
                ] as const).map(v => (
                  <button
                    key={v.id}
                    title={v.title}
                    onClick={() => { setTableView(v.id); localStorage.setItem("cat_tableView", v.id); setPage(1) }}
                    className={["px-2.5 py-1.5 transition-colors", tableView === v.id ? "bg-foreground text-background" : "hover:bg-secondary"].join(" ")}
                  >{v.icon}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>{/* end sticky */}

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-lg px-3 py-2">
          <AlertTriangle className="size-4 shrink-0" />{error}
        </div>
      )}

      {/* Diff panel */}
      {diff && (
        <div className="border border-border rounded-xl p-5 flex flex-col gap-4">
          <p className="text-sm font-semibold">Différences détectées</p>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="flex flex-col items-center p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
              <span className="text-xl font-bold text-green-600 dark:text-green-400">{diff.added.length}</span>
              <span className="text-xs text-muted-foreground">nouveaux</span>
            </div>
            <div className="flex flex-col items-center p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
              <span className="text-xl font-bold text-amber-600 dark:text-amber-400">{diff.modified.length}</span>
              <span className="text-xs text-muted-foreground">modifiés</span>
            </div>
            <div className="flex flex-col items-center p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
              <span className="text-xl font-bold text-red-600 dark:text-red-400">{diff.removed.length}</span>
              <span className="text-xs text-muted-foreground">supprimés</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{diff.unchanged} produits inchangés</p>

          {/* Detail list */}
          {[...diff.added, ...diff.modified, ...diff.removed].length > 0 && (
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border text-xs">
              {diff.added.map(d => (
                <div key={d.ref_key} className="flex items-center gap-2 px-3 py-2">
                  <span className="text-green-600 dark:text-green-400 font-medium w-16 shrink-0">+ Nouveau</span>
                  <span className="font-mono">{d.ref_key.replace(/ /g, '_')}</span>
                </div>
              ))}
              {diff.modified.map(d => (
                <div key={d.ref_key} className="flex items-center gap-2 px-3 py-2">
                  <span className="text-amber-600 dark:text-amber-400 font-medium w-16 shrink-0">✏ Modifié</span>
                  <span className="font-mono">{d.ref_key.replace(/ /g, '_')}</span>
                </div>
              ))}
              {diff.removed.map(d => (
                <div key={d.ref_key} className="flex items-center gap-2 px-3 py-2">
                  <span className="text-red-600 dark:text-red-400 font-medium w-16 shrink-0">− Supprimé</span>
                  <span className="font-mono">{d.ref_key.replace(/ /g, '_')}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" className="rounded-full" disabled={applyLoading}
              onClick={() => handleApply(["added", "modified", "removed"])}>
              {applyLoading ? <Loader2 className="size-3.5 animate-spin mr-1.5" /> : null}
              Appliquer tout
            </Button>
            {diff.added.length > 0 && (
              <Button size="sm" variant="outline" className="rounded-full" disabled={applyLoading}
                onClick={() => handleApply(["added"])}>
                Seulement les nouveaux ({diff.added.length})
              </Button>
            )}
            {diff.modified.length > 0 && (
              <Button size="sm" variant="outline" className="rounded-full" disabled={applyLoading}
                onClick={() => handleApply(["modified"])}>
                Seulement les modifiés ({diff.modified.length})
              </Button>
            )}
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setDiff(null); setDiffFile(null) }}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Tree view: Marque > Forme > Ref > Produits */}
      {treeView && !loading && (
        <TreeView products={filtered} collapsed={collapsed} setCollapsed={setCollapsed} />
      )}

      {/* Product table */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="size-5 animate-spin mr-2" />Chargement…
        </div>
      )}
      {!treeView && !loading && (
        <div className={tableView === "scroll" ? "border border-border rounded-xl overflow-hidden" : "border border-border rounded-xl overflow-x-auto"}>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-12">#</th>
                <th className="text-left px-3 py-2 font-medium">Ref</th>
                <th className="text-left px-3 py-2 font-medium">Marque</th>
                <th className="text-left px-3 py-2 font-medium">Produit</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Base ref</th>
                <th className="text-left px-3 py-2 font-medium">Sexe</th>
                <th className="text-left px-3 py-2 font-medium">Forme</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-center px-3 py-2 font-medium">Upload</th>
                <th className="text-center px-3 py-2 font-medium">Images</th>
                <th className="text-left px-3 py-2 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-8 text-muted-foreground text-xs">Aucun produit trouvé</td></tr>
              ) : pagedItems
                /* PAGE view — flat list, paginated */
                ? pagedItems.map(p => <ProductRow key={p.id} p={p} collapsed={collapsed} setCollapsed={setCollapsed} onPatch={patchProduct} />)
                /* SCROLL view — grouped */
                : grouped.map(({ label, items }) => (
                  <>
                    {label !== null && (
                      <tr
                        key={`g-${label}`}
                        className="bg-secondary/60 cursor-pointer select-none hover:bg-secondary"
                        onClick={() => setCollapsed(prev => {
                          const next = new Set(prev)
                          if (next.has(label)) next.delete(label); else next.add(label)
                          localStorage.setItem("cat_collapsed", JSON.stringify([...next]))
                          return next
                        })}
                      >
                        <td colSpan={11} className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          <span className="flex items-center gap-1.5">
                            {collapsed.has(label) ? <ChevronRight className="size-3" /> : <ChevronDown className="size-3" />}
                            {label}
                            <span className="font-normal normal-case text-muted-foreground/60">({items.length})</span>
                          </span>
                        </td>
                      </tr>
                    )}
                    {!collapsed.has(label ?? "") && items.map(p => <ProductRow key={p.id} p={p} collapsed={collapsed} setCollapsed={setCollapsed} onPatch={patchProduct} />)}
                  </>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination controls — page view only */}
      {!loading && tableView === "page" && groupBy === "none" && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 py-1">
          <span className="text-xs text-muted-foreground tabular-nums">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} / {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)} disabled={page === 1}
              className="px-2 py-1 rounded text-xs border border-border hover:bg-secondary disabled:opacity-30 transition-colors"
            >«</button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-2 py-1 rounded text-xs border border-border hover:bg-secondary disabled:opacity-30 transition-colors"
            >‹</button>
            <span className="flex items-center gap-1 text-xs">
              <span className="text-muted-foreground">Page</span>
              <input
                type="number" min={1} max={totalPages} value={page}
                onChange={e => { const v = Math.max(1, Math.min(totalPages, Number(e.target.value))); if (!isNaN(v)) setPage(v) }}
                className="w-12 text-center h-6 rounded border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-foreground/30 tabular-nums"
              />
              <span className="text-muted-foreground">/ {totalPages}</span>
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-2 py-1 rounded text-xs border border-border hover:bg-secondary disabled:opacity-30 transition-colors"
            >›</button>
            <button
              onClick={() => setPage(totalPages)} disabled={page === totalPages}
              className="px-2 py-1 rounded text-xs border border-border hover:bg-secondary disabled:opacity-30 transition-colors"
            >»</button>
          </div>
          <span className="text-xs text-muted-foreground">{PAGE_SIZE} / page</span>
        </div>
      )}

      {!loading && tableView === "scroll" && filtered.length > 0 && (
        <p className="text-xs text-muted-foreground">{filtered.length} produit{filtered.length > 1 ? "s" : ""}{search ? " filtrés" : ""}</p>
      )}
    </div>
  )
}

// ── Page root ─────────────────────────────────────────────────────────────────

export function CataloguePage({ initialCatalogueId }: { initialCatalogueId?: number }) {
  const [selected, setSelected] = useState<CatalogueDTO | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [globalSearch, setGlobalSearch] = useState("")
  const [searchResults, setSearchResults] = useState<CatalogueSearchResultDTO[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!initialCatalogueId) return
    catalogueApi.list().then(list => {
      const cat = list.find(c => c.id === initialCatalogueId)
      if (cat) setSelected(cat)
    }).catch(() => {})
  }, [initialCatalogueId])

  const handleGlobalSearch = useCallback((q: string) => {
    setGlobalSearch(q)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    // When inside a catalogue, search is local — no global API call
    if (selected) return
    if (q.length < 2) { setSearchResults([]); return }
    searchTimer.current = setTimeout(() => {
      setSearchLoading(true)
      catalogueApi.search(q)
        .then(setSearchResults)
        .catch(() => { setSearchResults([]); toast.error("Search failed") })
        .finally(() => setSearchLoading(false))
    }, 300)
  }, [selected])

  const showSearchResults = globalSearch.length >= 2

  return (
    <div className="flex flex-col h-full">
      {/* Single search bar */}
      <div className="px-4 pt-4 pb-2 shrink-0">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={globalSearch}
            onChange={e => handleGlobalSearch(e.target.value)}
            placeholder={selected ? `Rechercher dans ${selected.name}…` : "Rechercher dans tous les catalogues…"}
            className="w-full pl-9 pr-8 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
          />
          {globalSearch && (
            <button
              onClick={() => { setGlobalSearch(""); setSearchResults([]) }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {/* Global search results — only when no catalogue open */}
      {showSearchResults && !selected && (
        <div className="flex-1 overflow-auto px-4 pb-4">
          {searchLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
              <Loader2 className="size-4 animate-spin" /> Recherche…
            </div>
          ) : searchResults.length === 0 ? (
            <p className="py-8 text-sm text-muted-foreground">Aucun résultat pour « {globalSearch} »</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-2">{searchResults.length} résultat{searchResults.length > 1 ? "s" : ""}</p>
              <div className="border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-muted-foreground text-xs">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Catalogue</th>
                      <th className="text-left px-3 py-2 font-medium w-12">#</th>
                      <th className="text-left px-3 py-2 font-medium">Ref</th>
                      <th className="text-left px-3 py-2 font-medium">Marque</th>
                      <th className="text-left px-3 py-2 font-medium">Produit</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Base ref</th>
                      <th className="text-left px-3 py-2 font-medium">Sexe</th>
                      <th className="text-left px-3 py-2 font-medium">Forme</th>
                      <th className="text-left px-3 py-2 font-medium">Type</th>
                      <th className="text-center px-3 py-2 font-medium">Upload</th>
                      <th className="text-center px-3 py-2 font-medium">Images</th>
                      <th className="text-left px-3 py-2 font-medium">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {searchResults.map(r => (
                      <ProductRow
                        key={r.id}
                        p={r}
                        collapsed={new Set()}
                        setCollapsed={() => {}}
                        onPatch={(id, updates) => setSearchResults(prev => prev.map(x => x.id === id ? { ...x, ...updates } : x))}
                        catalogueName={r.catalogue_name}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Normal catalogue list / detail view */}
      {(!showSearchResults || selected) && (
        <div className="flex-1 overflow-auto">
          {selected ? (
            <CatalogueDetail
              catalogue={selected}
              onBack={() => { setSelected(null); setGlobalSearch(""); setSearchResults([]) }}
              onUpdated={() => setRefreshKey(k => k + 1)}
              search={globalSearch}
            />
          ) : (
            <CatalogueList
              key={refreshKey}
              onOpen={setSelected}
              onImported={() => setRefreshKey(k => k + 1)}
            />
          )}
        </div>
      )}
    </div>
  )
}
